import { useState } from 'react';

import { X } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle,
} from 'components/ui/dialog';
import ConfirmationDialog from './ui/ConfirmationDialog';

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';

export default function ReviewCartPopup({
    open,
    onClose,
    onContinue,
    onStartOver,
    onRemoveSeat,
    seatsPicked,
    chartSelectionErrors,
    currency = 'CAD',
}) {
    const [seatToRemove, setSeatToRemove] = useState(null);

    const { i18n } = useLingui();

    const formatCurrency = (value) => i18n.number(value, { style: 'currency', currency });

    const disableContinue = seatsPicked.length === 0 || chartSelectionErrors?.length > 0;
    const hasChartErrors = chartSelectionErrors?.length > 0;

    function handleRemoveClick(seat) {
        setSeatToRemove(seat);
    }

    function handleConfirmRemove() {
        if (seatToRemove) {
            onRemoveSeat(seatToRemove);
        }
        setSeatToRemove(null);
    }

    function handleCancelRemove() {
        setSeatToRemove(null);
    }

    const total = seatsPicked.reduce((acc, current) => {
        let price = 0;

        if (current.selectedTicketType && current.pricing?.ticketTypes) {
            const selectedType = current.pricing.ticketTypes.find(
                (tt) => tt.ticketType === current.selectedTicketType,
            );
            price = selectedType?.price ?? 0;
        } else {
            price = current.pricing?.price ?? current.price ?? 0;
        }

        acc += price;
        return acc;
    }, 0);

    const priceSummary = seatsPicked.reduce((acc, current) => {
        const seatCategory = current.category?.label || '';

        let price = 0;
        let ticketTypeLabel = 'Ticket';

        if (current.selectedTicketType && current.pricing?.ticketTypes) {
            const selectedType = current.pricing.ticketTypes.find(
                (tt) => tt.ticketType === current.selectedTicketType,
            );
            price = selectedType?.price ?? 0;
            ticketTypeLabel = selectedType?.label ?? 'Ticket';
        } else {
            price = current.pricing?.price ?? current.price ?? 0;
            ticketTypeLabel = 'Ticket';
        }

        const key = `${ticketTypeLabel}-${seatCategory}-${price}`;

        const existing = acc.find((s) => s.key === key);

        if (!existing) {
            acc.push({
                key: key,
                ticketType: ticketTypeLabel,
                seatCategory: seatCategory,
                price: price,
                amount: 1,
            });
            return acc;
        }

        return acc.map((s) => {
            if (s.key === key) {
                return {
                    ...s,
                    amount: s.amount + 1,
                };
            }
            return s;
        });
    }, []);

    return (
        <>
            <Dialog
                open={open}
                onOpenChange={(nextOpen) => {
                    if (!nextOpen) {
                        onClose();
                    }
                }}
            >
                <DialogContent className="flex max-h-[min(90vh,36rem)] w-[calc(100vw-2rem)] max-w-lg flex-col gap-0 overflow-hidden border-border bg-card p-0 text-card-foreground shadow-xl sm:rounded-xl">
                    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-6 py-4">
                        <DialogTitle className="m-0 text-left text-lg font-semibold leading-tight">
                            <Trans>Review Your Selection</Trans>
                        </DialogTitle>
                        <DialogClose asChild>
                            <button
                                type="button"
                                className="inline-flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                aria-label={t`Close`}
                            >
                                <X className="h-5 w-5" aria-hidden />
                            </button>
                        </DialogClose>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto p-3 md:p-6">
                        {hasChartErrors ? (
                            <Alert variant="warning" className="mb-4">
                                <AlertTitle>
                                    <Trans>Please adjust your seat selection</Trans>
                                </AlertTitle>
                                <AlertDescription>
                                    <Trans>
                                        We can't complete your purchase if it leaves an empty seat
                                        between booked seats. Please adjust your seats to continue.
                                    </Trans>
                                </AlertDescription>
                            </Alert>
                        ) : null}

                        <section className="mb-4 rounded-lg border border-border bg-background p-4 text-foreground">
                            <h4 className="m-0 mb-3 text-base font-medium leading-snug">
                                <Trans>Selected Seats: {seatsPicked.length}</Trans>
                            </h4>
                            {seatsPicked.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                    {seatsPicked.map((seat, index) => (
                                        <SeatLabel
                                            key={index}
                                            type={seat.objectType}
                                            parentType={seat.parent?.type}
                                            section={seat.labels?.section}
                                            row={seat.labels?.parent}
                                            seat={seat.labels?.own}
                                            onRemove={() => handleRemoveClick(seat)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <p className="m-0 text-sm italic text-muted-foreground">
                                    <Trans>No seats selected</Trans>
                                </p>
                            )}
                        </section>

                        <section className="rounded-lg border border-border bg-background p-4 text-foreground">
                            <h4 className="m-0 mb-3 text-base font-medium leading-snug">
                                <Trans>Price Summary</Trans>
                            </h4>
                            {priceSummary.length > 0 ? (
                                <>
                                    {priceSummary.map((el) => (
                                        <div
                                            key={el.key}
                                            className="mb-2 flex justify-between gap-3 text-sm last:mb-0"
                                        >
                                            <div className="flex min-w-0 flex-wrap items-baseline gap-x-1">
                                                <span>{el.ticketType}</span>
                                                {el.seatCategory ? (
                                                    <span className="text-muted-foreground">
                                                        ({el.seatCategory})
                                                    </span>
                                                ) : null}
                                                <span className="text-muted-foreground">
                                                    x {el.amount}
                                                </span>
                                            </div>
                                            <span className="shrink-0 tabular-nums">
                                                {formatCurrency((el.amount * el.price).toFixed(2))}
                                            </span>
                                        </div>
                                    ))}
                                    <div className="mt-3 flex justify-between border-t border-border pt-3 text-base font-semibold">
                                        <span>
                                            <Trans>Total</Trans>
                                        </span>
                                        <span className="tabular-nums">
                                            {formatCurrency(total.toFixed(2))}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <p className="m-0 text-sm italic text-muted-foreground">
                                    <Trans>Select seats to see pricing</Trans>
                                </p>
                            )}
                        </section>
                    </div>

                    <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            className="h-10 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive sm:mr-0"
                            onClick={onStartOver}
                        >
                            <Trans>Clear Selection</Trans>
                        </Button>
                        <Button
                            type="button"
                            variant="default"
                            onClick={onContinue}
                            disabled={disableContinue}
                            className="h-10 font-bold uppercase tracking-wide shadow-none disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100"
                        >
                            <Trans>Continue</Trans>
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
            <ConfirmationDialog
                open={seatToRemove !== null}
                onClose={handleCancelRemove}
                onConfirm={handleConfirmRemove}
                title={t`Remove Seat?`}
                message={t`This seat will be released and may be taken by someone else. Are you sure you want to remove it?`}
                confirmLabel={t`Remove`}
                cancelLabel={t`Cancel`}
            />
        </>
    );
}

function SeatLabel(props) {
    const { type, parentType, section, row, seat, onRemove } = props;

    const seatLabelValues = [];

    if (section) {
        seatLabelValues.push(`Sec ${section}`);
    }
    if (row) {
        const rowType = parentType === 'table' ? t`Table` : t`Row`;
        seatLabelValues.push(`${rowType} ${row}`);
    }
    if (seat) {
        const seatType = (() => {
            if (type === 'Table') return t`Table`;
            if (type === 'Seat') return t`Seat`;
            return '';
        })();
        seatLabelValues.push(`${seatType} ${seat}`);
    }

    const seatLabel = seatLabelValues.length ? seatLabelValues.join(' • ') : '';

    return (
        <div className="inline-flex max-w-full items-center gap-0.5 rounded-2xl border border-border bg-card text-xs font-normal text-foreground">
            <span className="min-w-0 truncate pl-2">{seatLabel}</span>
            <button
                type="button"
                onClick={onRemove}
                className="inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-2xl border-0 bg-transparent p-0 text-muted-foreground shadow-none outline-none transition-colors [-webkit-tap-highlight-color:transparent] hover:bg-white/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 dark:hover:bg-white/10"
                aria-label={t`Remove seat`}
            >
                <X className="h-3.5 w-3.5 stroke-[1.75]" aria-hidden />
            </button>
        </div>
    );
}
